import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    EyeIcon,
    Search,
} from "lucide-react";
import { Button } from "@component/button";
import { Input } from "@component/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@component/table";
import BreadcrumbNav from "@component/breadcrumb-nav";
import { Job } from "@type/job";
import { AssessmentSubmissions, FormAssessment, InterviewAssessment } from "@type/assessments";
import { getAssessmentDetail, getJobAssessmentSubmission } from "@service/assessment";
import { getInterviewInsights } from "@service/interview";
import { catchErrorLog } from "@util/sentry";
import { useSnackbar } from "@hook/use-snackbar";
import { getJobDetail } from "@service/job";
import { defaultPagination } from "@constant/app";
import { parseFormat } from "@util/date";
import { getStatusBadgeClass } from "@util/string";
import { Badge } from "@component/badge";
import DrawerAIInterview from "@container/drawer/drawer-ai-interview";
import { cn } from "@util/tailwind";
import { InterviewInsight } from "@type/videoInterview";

export default function SubmissionSection() {
    const { assessment_id, job_id } = useParams();
    const snackbar = useSnackbar();
    const [searchQuery, setSearchQuery] = useState("");
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const [isLoadingAssessment, setIsLoadingAssessment] = useState<boolean>(false);
    const [currentJob, setCurrentJob] = useState<Job | undefined>(undefined);
    const [submissions, setSubmissions] = useState<AssessmentSubmissions[]>([]); // Replace 'any' with your submission type
    const [isAssessmentInterview, setIsAssessmentInterview] = useState<boolean>(false); // Assume true for demo
    const [selectedSubmission, setSelectedSubmission] = useState<AssessmentSubmissions | null>(null);
    const [interviewAssessment, setInterviewAssessment] = useState<InterviewAssessment | undefined>(undefined);
    const [formAssessment, setFormAssessment] = useState<FormAssessment | undefined>(undefined);
    const [filterStatus, setFilterStatus] = useState<string[]>([]);
    const [interviewScores, setInterviewScores] = useState<Record<string, number | null>>({});

    useEffect(() => {
        if (assessment_id) {
            setIsLoadingAssessment(true);
            getAssessmentDetail(assessment_id).then((resp) => {
                if (resp.status === 200) {
                    setIsAssessmentInterview(resp.data.data.type === "Interview" ? true : false);
                    if (resp.data.data.type === "Interview") {
                        setInterviewAssessment(resp.data.data as InterviewAssessment);
                    } else {
                        setFormAssessment(resp.data.data as FormAssessment);
                    }
                }
            }).catch((error) => {
                snackbar.showError("Failed to fetch screening details. Please try again.");
                catchErrorLog(`Failed to fetch assessment details for id ${assessment_id}, error: ${error}`);
            }).finally(() => {
                setIsLoadingAssessment(false);
            })
        }
    }, [assessment_id, isAssessmentInterview])

    useEffect(() => {
        if (job_id) {
            getJobDetail(job_id).then((resp) => {
                if (resp) {
                    setCurrentJob(resp);
                }
            }).catch((error) => {
                catchErrorLog(`Failed to fetch job details for id ${job_id}, error: ${error}`);
            })
        }
    }, [job_id])

    useEffect(() => {
        if (assessment_id && job_id) {
            const savedFilters = localStorage.getItem("interviewFilters") ? JSON.parse(localStorage.getItem("interviewFilters") || '[]') : [];
            setFilterStatus(savedFilters);
            fetchSubmission("", 1, savedFilters);
        }
    }, [assessment_id, job_id])

    const fetchInterviewScores = (submissions: AssessmentSubmissions[]) => {
        submissions.forEach((submission) => {
            const conversationId = submission.answers?.[0]?.content;
            if (!conversationId && submission.answers?.[0]?.id !== "conversation_id") return;

            getInterviewInsights(conversationId)
                .then((res) => {
                    if (res?.insights) {
                        const parsed: InterviewInsight = JSON.parse(res.insights);
                        setInterviewScores((prev) => ({
                            ...prev,
                            [submission.jobseeker_id]: parsed.total_interview_score ?? null,
                        }));
                    }
                })
                .catch((err) => {
                    catchErrorLog(`Failed to fetch interview insights for ${submission.jobseeker_id}: ${err}`);
                });
        });
    };

    const fetchSubmission = (keyword: string, page: number, status: string[]) => {
        getJobAssessmentSubmission({
            keyword: keyword || "",
            page: page,
            limit: defaultPagination.limit,
            assessment_id: assessment_id || "",
            job_id: job_id || "",
            submission_status: "Complete",
        }).then((resp) => {
            if (resp.status === 200) {
                setSubmissions(resp.data.data);
                fetchInterviewScores(resp.data.data);
            }
        }).catch((error) => {
            snackbar.showError("Failed to fetch submissions. Please try again.");
            catchErrorLog(`Failed to fetch submissions for assessment id ${assessment_id} and job id ${job_id}, error: ${error}`);
        })
    }

    const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        fetchSubmission(searchQuery, 1, []);
    }

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        if (e.target.value === "") {
            fetchSubmission("", 1, []);
        }
    }

    const handleViewResponses = (submission: AssessmentSubmissions) => {
        setIsDrawerOpen(true);
        setSelectedSubmission(submission);
    };

    const handleCloseDrawer = () => {
        setIsDrawerOpen(false);
    };

    return (
        <div className="h-screen bg-white flex -m-8">
            <div
                className={`transition-all duration-300 ${isDrawerOpen ? "w-3/5" : "w-full"
                    } h-full flex flex-col overflow-hidden`}
            >
                <div
                    className={cn("flex-1 overflow-y-auto p-6 space-y-8", {
                        "mr-6": isDrawerOpen,
                    })}
                    style={{
                        paddingTop: "32px",
                        paddingBottom: "32px",
                        paddingRight: "32px",
                        paddingLeft: "32px",
                    }}
                >
                    {/* Breadcrumb Navigation */}
                    <div className="flex items-center justify-between">
                        <BreadcrumbNav
                            items={[
                                { label: "Screen", href: "/assess" },
                                {
                                    label: isAssessmentInterview ? interviewAssessment?.content.interviewName || "" : formAssessment?.content.title || "",
                                    href: `/assess/${assessment_id}/${isAssessmentInterview ? "interview" : "form"}`
                                },
                                { label: currentJob?.job_title || "Job" },
                                { label: "Submissions" },
                            ]}
                        />
                    </div>

                    {/* Header */}
                    <div className="space-y-6">
                        <div>
                            <h1 className="text-lg font-semibold text-gray-900">
                                Submissions for {currentJob?.job_title}
                            </h1>
                            <p className="text-sm text-gray-600 mt-1">
                                {submissions.length} total submission{submissions.length !== 1 ? "s" : ""}
                            </p>
                        </div>

                        {/* Search and Filter Bar */}
                        <form onSubmit={handleSearchSubmit} className="relative flex-1 flex flex-row gap-4">
                            <div className="relative flex-grow">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <Input
                                    type="text"
                                    placeholder="Search candidates..."
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    className="pl-10 bg-white border-gray-200 w-full hover:border-blue-600"
                                />
                            </div>
                            {/* {isAssessmentInterview && (
                                <InterviewFilterPanel
                                    filters={filterStatus}
                                    onFiltersChange={handleOnFiltersChange}
                                />
                            )} */}
                        </form>
                    </div>

                    {/* Submissions Table */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-gray-50">
                                    <TableHead
                                        className="text-sm font-medium text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
                                    >
                                        <div className="flex items-center gap-2">
                                            Candidate
                                        </div>
                                    </TableHead>
                                    <TableHead
                                        className="text-sm font-medium text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
                                    >
                                        <div className="flex items-center gap-2">
                                            Email
                                        </div>
                                    </TableHead>
                                    <TableHead
                                        className="text-sm font-medium text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
                                    >
                                        <div className="flex items-center gap-2">
                                            Submission Date
                                        </div>
                                    </TableHead>
                                    {!isAssessmentInterview && (
                                        <TableHead
                                            className="text-sm font-medium text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
                                        >
                                            <div className="flex items-center gap-2">
                                                Form Score
                                            </div>
                                        </TableHead>
                                    )}
                                    {isAssessmentInterview && (
                                        <TableHead className="text-sm font-medium text-gray-900">
                                            Interview Status
                                        </TableHead>
                                    )}
                                    {isAssessmentInterview && (
                                        <TableHead className="text-sm font-medium text-gray-900">
                                            Interview Score
                                        </TableHead>
                                    )}
                                    <TableHead className="text-sm font-medium text-gray-900">
                                        Actions
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {submissions.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={isAssessmentInterview ? 6 : 4} className="text-center py-6">
                                            <p className="text-gray-500">No submissions found.</p>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {submissions.map((submission, index) => {
                                    return (
                                        <TableRow key={index} className="hover:bg-gray-50">
                                            <TableCell className="text-sm font-medium">
                                                {submission.jobseeker_name}
                                            </TableCell>
                                            <TableCell className="text-sm text-gray-600">
                                                {submission.jobseeker_email}
                                            </TableCell>
                                            <TableCell className="text-sm text-gray-600">
                                                {parseFormat(submission.updated_at, "D MMM YYYY h:mm A")}
                                            </TableCell>
                                            {!isAssessmentInterview && (
                                                <TableCell>
                                                    {submission.score ? submission.score : "-"}
                                                </TableCell>
                                            )}
                                            {isAssessmentInterview && (
                                                <TableCell>
                                                    <Badge
                                                        className={`mt-1 !shadow-none rounded-xl ${getStatusBadgeClass(submission.status)}`}
                                                    >
                                                        {submission.status}
                                                    </Badge>
                                                </TableCell>
                                            )}
                                            {isAssessmentInterview && (
                                                <TableCell className="text-sm text-gray-600">
                                                    {interviewScores[submission.jobseeker_id] !== undefined
                                                        ? interviewScores[submission.jobseeker_id] !== null
                                                            ? `${interviewScores[submission.jobseeker_id]}/100`
                                                            : "-"
                                                        : <span className="text-gray-400">Loading...</span>
                                                    }
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="flex items-center gap-2"
                                                    onClick={() => handleViewResponses(submission)}
                                                >
                                                    <EyeIcon className="h-4 w-4" />
                                                    View Responses
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </div>

            {/* Submission Details Drawer */}
            {(selectedSubmission && selectedSubmission && isDrawerOpen) && (
                <div className="w-2/5 h-full fixed right-0 top-0 bg-white z-50">
                    <DrawerAIInterview
                        isOpen={isDrawerOpen}
                        onClose={handleCloseDrawer}
                        assessment_id={assessment_id || ""}
                        user_id={selectedSubmission.jobseeker_id}
                        submission={selectedSubmission}
                        assessment={isAssessmentInterview ? interviewAssessment : formAssessment}
                        isAIInterview={isAssessmentInterview}
                    />
                </div>
            )}
        </div>
    );
}
